// ============================================================
// Leaderboard Page - Candidate ranking for a job
// ============================================================

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ArrowRight, Download, Trophy, Medal, Award } from 'lucide-react';
import toast from 'react-hot-toast';

export function LeaderboardPage() {
    const { jobId } = useParams();

    const { data, isLoading } = useQuery({
        queryKey: ['leaderboard', jobId],
        queryFn: () => api.get(`/dashboard/jobs/${jobId}/leaderboard`),
    });

    const entries = data?.data || [];

    const handleExportCSV = async () => {
        try {
            const apiBase = import.meta.env.VITE_API_URL || '/api';
            const token = localStorage.getItem('token');
            const res = await fetch(`${apiBase}/dashboard/jobs/${jobId}/export`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `leaderboard-${jobId}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('CSV exported!');
        } catch (err: any) {
            toast.error(err.message || 'Export failed');
        }
    };

    const getRankIcon = (rank: number) => {
        if (rank === 1) return <Trophy size={18} color="#f59e0b" />;
        if (rank === 2) return <Medal size={18} color="#9ca3af" />;
        if (rank === 3) return <Award size={18} color="#cd7f32" />;
        return <span style={{ color: '#6b7280', fontWeight: 600, fontSize: '14px', width: 18, textAlign: 'center', display: 'inline-block' }}>{rank}</span>;
    };

    const getScoreClass = (score: number) => {
        if (score >= 70) return 'score-high';
        if (score >= 40) return 'score-mid';
        return 'score-low';
    };

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <Link to={`/jobs/${jobId}`} style={{ color: '#6b7280', fontSize: '14px' }}>← Back to Job</Link>
                    <h1 className="mt-2">Candidate Leaderboard</h1>
                    <p className="subtitle">Ranked by composite assessment score</p>
                </div>
                <button className="btn btn-secondary" onClick={handleExportCSV}>
                    <Download size={16} /> Export CSV
                </button>
            </div>

            <div className="card">
                {entries.length === 0 ? (
                    <div className="empty-state">
                        <div className="icon">🏆</div>
                        <h3>No scored candidates yet</h3>
                        <p>Candidates will appear here after completing assessments</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>Rank</th>
                                <th>Candidate</th>
                                <th>Stage</th>
                                <th>MCQ Score</th>
                                <th>Subjective</th>
                                <th>Composite</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry: any) => (
                                <tr key={entry.candidateRecord.id}>
                                    <td style={{ textAlign: 'center' }}>{getRankIcon(entry.rank)}</td>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{entry.candidate.name}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{entry.candidate.email}</div>
                                    </td>
                                    <td>
                                        <span className="badge badge-accent">{entry.currentStage?.name || '—'}</span>
                                    </td>
                                    <td>
                                        {entry.score ? (
                                            <div>
                                                <span style={{ fontWeight: 600 }}>{(entry.score.mcqScore || 0).toFixed(0)}%</span>
                                                <div className="score-bar mt-2" style={{ width: 80 }}>
                                                    <div className={`score-bar-fill ${getScoreClass(entry.score.mcqScore || 0)}`}
                                                        style={{ width: `${entry.score.mcqScore || 0}%` }} />
                                                </div>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        {entry.score ? (
                                            <div>
                                                <span style={{ fontWeight: 600 }}>{(entry.score.subjectiveScore || 0).toFixed(0)}%</span>
                                                <div className="score-bar mt-2" style={{ width: 80 }}>
                                                    <div className={`score-bar-fill ${getScoreClass(entry.score.subjectiveScore || 0)}`}
                                                        style={{ width: `${entry.score.subjectiveScore || 0}%` }} />
                                                </div>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        {entry.score ? (
                                            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>
                                                <span style={{
                                                    background: `linear-gradient(135deg, ${(entry.score.compositeScore || 0) >= 70 ? '#10b981, #34d399' :
                                                        (entry.score.compositeScore || 0) >= 40 ? '#f59e0b, #fbbf24' :
                                                            '#ef4444, #f87171'
                                                        })`,
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                }}>
                                                    {(entry.score.compositeScore || 0).toFixed(1)}%
                                                </span>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <Link to={`/candidates/${entry.candidateRecord.id}`} className="btn btn-ghost btn-sm">
                                            Profile <ArrowRight size={14} />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
